import { DatePicker, Dropdown, EchartsWithTheme, Switch, Tooltip, useTheme } from '@maxtropy/components';
import {
  apiV2LoadDispatchCenterKeyIndicatorsPost,
  apiV2MicroElectricOverviewPowerTrendSeriesPost,
} from '@maxtropy/dmes-apis-v2';
import { useRequest } from 'ahooks';
import { Progress } from 'antd';
import dayjs from 'dayjs';
import { useMemo, useRef, useState } from 'react';
import { getCurrentTimeMark, useCurrentTimeMinute } from '../../../MicrogridOverview/components/currentTimeMark';
import { convertPower, formatPower, getPowerUnit } from '../../utils';
import type { PowerUnit } from '../../utils';
import SectionTitle from '../SectionTitle';
import styles from './index.module.scss';

type CommunicationAlarmStatus = 'none' | 'unrecovered' | 'recovered';

interface MetricItem {
  label: string;
  value: string;
  tooltipValue: string;
  communicationAlarmStatus: CommunicationAlarmStatus;
}

interface TrendTooltipParam {
  axisValueLabel?: string;
  marker?: string;
  seriesName: string;
  value?: number | string | null;
}

const seriesMeta = [
  { name: '光伏', field: 'pvPowerSeries', color: '#16DD8E', areaColor: 'rgba(22, 221, 142, 0.16)' },
  { name: '市电', field: 'gridPowerSeries', color: '#21A8FF', areaColor: 'rgba(33, 168, 255, 0.12)' },
  { name: '火电', field: 'thermalPowerSeries', color: '#FFD45A', areaColor: 'rgba(255, 212, 90, 0.12)' },
  { name: '储能', field: 'storagePowerSeries', color: '#C28BE8', areaColor: 'rgba(194, 139, 232, 0.12)' },
  { name: '用电负荷', field: 'loadPowerSeries', color: '#FF4FA2', areaColor: undefined },
] as const;

const escapeHtml = (value: string | number) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const hasTooltipValue = (param: TrendTooltipParam) =>
  param.value !== null && param.value !== undefined && param.value !== '-';

const formatTrendTooltip = (params: TrendTooltipParam | TrendTooltipParam[], unit: PowerUnit) => {
  const paramList = Array.isArray(params) ? params : [params];
  const rows = seriesMeta.flatMap(item => {
    const seriesParam =
      paramList.find(param => param.seriesName === item.name && hasTooltipValue(param)) ??
      paramList.find(param => param.seriesName === `${item.name}预测` && hasTooltipValue(param));
    const numberValue = Number(seriesParam?.value);

    return seriesParam && Number.isFinite(numberValue)
      ? [
          `<div style="display:flex;justify-content:space-between;gap:24px;line-height:24px;">` +
            `<span>${seriesParam.marker ?? ''}${escapeHtml(item.name)}</span>` +
            `<span style="min-width:88px;text-align:right;">${numberValue.toFixed(2)} ${unit}</span>` +
            `</div>`,
        ]
      : [];
  });

  return `<div style="min-width:220px;"><div style="margin-bottom:4px;">${escapeHtml(
    paramList[0]?.axisValueLabel ?? ''
  )}</div>${rows.join('')}</div>`;
};

const formatRate = (value?: number) => (value == null ? '-' : `${value.toFixed(2)}%`);
const getProgressPercent = (value?: number) => Math.min(100, Math.max(0, value ?? 0));
const getCommunicationAlarmStatus = (status?: number): CommunicationAlarmStatus =>
  status === 1 ? 'unrecovered' : 'none';

const CommunicationIndicator = ({ alarm }: { alarm: boolean }) => {
  const tooltip = alarm ? '通讯异常' : '通讯正常';

  return (
    <Tooltip
      getPopupContainer={() =>
        document.fullscreenElement instanceof HTMLElement ? document.fullscreenElement : document.body
      }
      title={<div style={{ color: alarm ? '#ff4d4f' : '#16dd8e' }}>{tooltip}</div>}
    >
      <i aria-label={tooltip} className={alarm ? styles.dangerDot : styles.normalDot} />
    </Tooltip>
  );
};

const OverflowTooltip = ({ text, className }: { text: string; className?: string }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  const checkOverflow = () => {
    const element = textRef.current;
    setIsOverflow(Boolean(element && element.scrollWidth > element.clientWidth));
  };

  return (
    <Tooltip title={isOverflow ? text : undefined}>
      <span ref={textRef} className={className} onMouseEnter={checkOverflow}>
        {text}
      </span>
    </Tooltip>
  );
};

const KeyMetricsMonitor = () => {
  const theme = useTheme();
  const [showForecast, setShowForecast] = useState(true);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const { data: indicatorData } = useRequest(() => apiV2LoadDispatchCenterKeyIndicatorsPost({}), {
    pollingInterval: 60 * 1000,
  });
  const { data: trendData } = useRequest(
    () =>
      apiV2MicroElectricOverviewPowerTrendSeriesPost({
        startTime: selectedDate.startOf('day').valueOf(),
        endTime: selectedDate.endOf('day').valueOf(),
      }),
    { refreshDeps: [selectedDate] }
  );
  const minuteOfDayValues = useMemo(
    () => (trendData?.timeSeries ?? []).map(timestamp => dayjs(timestamp).hour() * 60 + dayjs(timestamp).minute()),
    [trendData?.timeSeries]
  );
  const currentTimeMinute = useCurrentTimeMinute(minuteOfDayValues);
  const metrics = useMemo<MetricItem[]>(() => {
    const gridMeters = indicatorData?.gridMeterPowerList?.slice(0, 2) ?? [];
    const generatorUnits = indicatorData?.generatorUnitPowerList?.slice(0, 2) ?? [];
    const totalLoad = formatPower(indicatorData?.totalLoad?.power);
    const thermalPower = formatPower(indicatorData?.thermalPower?.power);
    const pvPower = formatPower(indicatorData?.pvPower?.power);
    const nartaTotalPower = formatPower(indicatorData?.nartaTotalPower?.power);
    const loadBaselineTarget = formatPower(indicatorData?.loadBaselineTarget);

    return [
      {
        label: `总负荷(${totalLoad.unit})`,
        value: totalLoad.value,
        tooltipValue: totalLoad.text,
        communicationAlarmStatus: getCommunicationAlarmStatus(indicatorData?.totalLoad?.communicationStatus),
      },
      {
        label: `火力发电(${thermalPower.unit})`,
        value: thermalPower.value,
        tooltipValue: thermalPower.text,
        communicationAlarmStatus: getCommunicationAlarmStatus(indicatorData?.thermalPower?.communicationStatus),
      },
      {
        label: `光伏发电(${pvPower.unit})`,
        value: pvPower.value,
        tooltipValue: pvPower.text,
        communicationAlarmStatus: getCommunicationAlarmStatus(indicatorData?.pvPower?.communicationStatus),
      },
      {
        label: `纳尔图总有功(${nartaTotalPower.unit})`,
        value: nartaTotalPower.value,
        tooltipValue: nartaTotalPower.text,
        communicationAlarmStatus: getCommunicationAlarmStatus(indicatorData?.nartaTotalPower?.communicationStatus),
      },
      ...gridMeters.map((meter, index) => {
        const power = formatPower(meter?.power);
        return {
          label: `${meter?.deviceName ?? `市电电表${index + 1}`}(${power.unit})`,
          value: power.value,
          tooltipValue: power.text,
          communicationAlarmStatus: getCommunicationAlarmStatus(meter?.communicationStatus),
        };
      }),
      ...generatorUnits.map((unit, index) => {
        const power = formatPower(unit?.power);
        return {
          label: `${unit?.unitName ?? `${index + 1}#机组`}负荷(${power.unit})`,
          value: power.value,
          tooltipValue: power.text,
          communicationAlarmStatus: getCommunicationAlarmStatus(unit?.communicationStatus),
        };
      }),
      {
        label: `负荷基线目标(${loadBaselineTarget.unit})`,
        value: loadBaselineTarget.value,
        tooltipValue: loadBaselineTarget.text,
        communicationAlarmStatus: getCommunicationAlarmStatus(),
      },
    ];
  }, [indicatorData]);
  const ratios = useMemo(() => {
    const generatorUnits = indicatorData?.generatorUnitPowerList?.slice(0, 2) ?? [];
    return [
      ...generatorUnits.map((unit, index) => ({
        label: `${unit?.unitName ?? `${index + 1}#机组`}用电率`,
        value: formatRate(unit?.powerRate),
        percent: getProgressPercent(unit?.powerRate),
      })),
      {
        label: '负荷匹配率',
        value: formatRate(indicatorData?.loadMatchRate),
        percent: getProgressPercent(indicatorData?.loadMatchRate),
      },
    ];
  }, [indicatorData]);
  const chartOption = useMemo(() => {
    const timestamps = trendData?.timeSeries ?? [];
    const labels = timestamps.map(timestamp => dayjs(timestamp).format('HH:mm'));
    const lastLabelIndex = Math.max(labels.length - 1, 0);
    const predictedTs = trendData?.predictedTs;
    const predictedIndex =
      typeof predictedTs === 'number' ? timestamps.findIndex(timestamp => timestamp >= predictedTs) : -1;
    const currentTimeIndex = minuteOfDayValues.findIndex(minute => minute === currentTimeMinute);
    const currentTimeLabel =
      selectedDate.isSame(dayjs(), 'day') && currentTimeIndex >= 0 ? labels[currentTimeIndex] : undefined;
    const powerValues = seriesMeta
      .flatMap(item => trendData?.[item.field] ?? [])
      .filter((value): value is number => typeof value === 'number');
    const powerUnit = getPowerUnit(powerValues);

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        confine: true,
        formatter: (params: TrendTooltipParam | TrendTooltipParam[]) => formatTrendTooltip(params, powerUnit),
      },
      legend: {
        show: true,
        top: 0,
        right: showForecast ? 70 : 20,
        itemWidth: 14,
        itemHeight: 4,
        itemGap: 16,
        selectedMode: true,
        textStyle: { color: 'rgba(255,255,255,0.85)', fontSize: 14 },
        data: seriesMeta.map(item => ({ name: item.name, icon: 'rect' })),
      },
      graphic: showForecast
        ? [
            {
              type: 'group',
              right: 20,
              top: 4,
              children: [
                {
                  type: 'line',
                  shape: { x1: 0, y1: 4, x2: 14, y2: 4 },
                  style: { stroke: '#FFFFFF', lineWidth: 3, lineDash: [3, 2] },
                },
                {
                  type: 'text',
                  left: 18,
                  top: -2,
                  style: {
                    text: '预测',
                    fill: 'rgba(255,255,255,0.85)',
                    font: '14px sans-serif',
                  },
                },
              ],
            },
          ]
        : [],
      dataZoom: [
        {
          type: 'inside',
          start: 0,
          end: 100,
        },
        {
          type: 'slider',
          start: 0,
          end: 100,
          bottom: 8,
          height: 14,
          brushSelect: false,
        },
      ],
      grid: { left: 15, right: 25, top: 55, bottom: 30, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLabel: {
          interval: (index: number, value: string) => value.endsWith(':00') || index === lastLabelIndex,
          formatter: (value: string, index: number) => {
            if (labels.length > 1 && index === lastLabelIndex) return '24:00';
            return value.replace(/^0/, '');
          },
          showMinLabel: true,
          showMaxLabel: true,
          hideOverlap: true,
          margin: 8,
        },
      },
      yAxis: {
        type: 'value',
        name: powerUnit,
        nameGap: 8,
        axisLabel: { margin: 8 },
      },
      series: [
        ...seriesMeta.flatMap(item => {
          const commonSeries = {
            name: item.name,
            type: 'line',
            smooth: true,
            showSymbol: false,
            connectNulls: false,
            lineStyle: { color: item.color, type: 'solid' },
            itemStyle: { color: item.color },
            areaStyle: item.areaColor ? { color: item.areaColor } : undefined,
          };
          const historySeries = {
            ...commonSeries,
            data: (trendData?.[item.field] ?? []).map((value, pointIndex) =>
              predictedIndex < 0 || pointIndex <= predictedIndex ? convertPower(value, powerUnit) : null
            ),
          };

          return [
            historySeries,
            ...(showForecast && predictedIndex >= 0
              ? [
                  {
                    ...commonSeries,
                    name: `${item.name}预测`,
                    lineStyle: { color: item.color, type: 'dashed' },
                    areaStyle: undefined,
                    data: (trendData?.[item.field] ?? []).map((value, pointIndex) =>
                      pointIndex >= predictedIndex ? convertPower(value, powerUnit) : null
                    ),
                  },
                ]
              : []),
          ];
        }),
        ...(currentTimeLabel
          ? [
              {
                name: '',
                type: 'line',
                data: [],
                silent: true,
                tooltip: { show: false },
                ...getCurrentTimeMark(currentTimeLabel),
              },
            ]
          : []),
      ],
    };
  }, [currentTimeMinute, minuteOfDayValues, selectedDate, showForecast, trendData]);

  return (
    <section className={styles.panel}>
      <SectionTitle>重点指标数据监测</SectionTitle>
      <div className={styles.monitorContent}>
        <div className={styles.metricsColumn}>
          <div className={styles.metricGrid}>
            {metrics.map(({ label, value, tooltipValue, communicationAlarmStatus }) => {
              const communicationNormal = communicationAlarmStatus !== 'unrecovered';
              return (
                <div className={styles.metric} key={label}>
                  <span className={styles.metricLabel}>
                    <OverflowTooltip text={label} className={styles.metricLabelText} />
                    <CommunicationIndicator alarm={!communicationNormal} />
                  </span>
                  <Tooltip title={tooltipValue}>
                    <strong>{value}</strong>
                  </Tooltip>
                </div>
              );
            })}
          </div>
          <div className={styles.ratioGrid}>
            {ratios.map(({ label, value, percent }) => (
              <div className={styles.ratio} key={label}>
                <div>
                  <OverflowTooltip text={label} />
                  <Tooltip title={value}>
                    <strong>{value}</strong>
                  </Tooltip>
                </div>
                <Progress
                  className={styles.ratioProgress}
                  type="circle"
                  width={24}
                  strokeWidth={12}
                  percent={percent}
                  showInfo={false}
                  strokeColor="var(--mx-success-color)"
                  trailColor="var(--mx-disabled-bg-color)"
                />
              </div>
            ))}
          </div>
        </div>
        <div className={styles.chartArea}>
          <div className={styles.chartToolbar}>
            <Dropdown
              type="link"
              className={styles.dropdownWrapper}
              getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
              trigger={['click']}
              dropdownRender={() => (
                <div className={styles.dropdownMenuWrapper}>
                  <DatePicker
                    picker="date"
                    value={selectedDate}
                    format="YYYY-MM-DD"
                    allowClear={false}
                    disabledDate={current => current.isAfter(dayjs(), 'day')}
                    getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
                    onChange={date => date && setSelectedDate(date)}
                  />
                </div>
              )}
            >
              {selectedDate.format('YYYY-MM-DD')}
            </Dropdown>
            <label>
              显示预测值 <Switch size="small" checked={showForecast} onChange={setShowForecast} />
            </label>
          </div>
          <EchartsWithTheme option={chartOption} theme={theme} style={{ height: 260 }} />
        </div>
      </div>
    </section>
  );
};

export default KeyMetricsMonitor;
